/**
 * A graphics asset on its asset page: the template running live in a sandboxed, network-less frame, with a
 * time slider, editable field values (saved as the defaults for new uses), and what the manifest says.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Pause, Play, Save } from 'lucide-react';
import {
  TICKS_PER_SECOND, frameRateLabel, msToFrameTicks, ografAuthoredRate, ografDefaultLengthMs, ografDefaults, ografFields, ografPosterMs, ografResolution, ografSchedule,
  ografStepCount, ografTimestamp, type AssetDetail, type GraphicData,
} from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useGraphicsSettings, useMe, useTemplateBundle } from '../api/hooks';
import { OgrafPreview } from '../graphics/ograf';
import { GraphicPreview } from './GraphicPreview';
import { toast } from './Toasts';
import { queryKeys } from '../api/queryKeys';

const KIND_LABEL = { lower_third: 'Lower third', title_card: 'Title card', logo: 'Logo', overlay: 'Overlay' } as const;

export function GraphicAsset({ asset }: { asset: AssetDetail }) {
  const g = asset.graphic!;
  if (g.engine === 'ograf-v1' && g.ograf) return <OgrafAsset asset={asset} />;
  return <TemplateAsset asset={asset} />;
}

/**
 * An OGraf graphic (0.93.0) on its asset page: the graphic in the OGraf host at a moment of a default-length clip,
 * the fields its schema has (to try values; its defaults are part of the graphic and change in PRISM Graphics),
 * and what its manifest says.
 */
function OgrafAsset({ asset }: { asset: AssetDetail }) {
  const g = asset.graphic!;
  const m = g.ograf!;
  const settings = useGraphicsSettings();
  const rate = ografAuthoredRate(m) ?? { num: 25, den: 1 };
  const durationTicks = msToFrameTicks(ografDefaultLengthMs(m, settings.data?.defaultHoldSeconds ?? 4), rate);
  const seconds = durationTicks / TICKS_PER_SECOND;
  const fields = useMemo(() => ografFields(m), [m]);
  const [data, setData] = useState<GraphicData>(() => ografDefaults(m));
  const schedule = useMemo(() => ografSchedule(m, { data, durationTicks }, rate), [m, data, durationTicks]); // eslint-disable-line react-hooks/exhaustive-deps
  const [t, setT] = useState(ografPosterMs(m, rate) / 1000);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!playing) return;
    const start = performance.now() - t * 1000;
    let raf = 0;
    const step = () => {
      const next = (performance.now() - start) / 1000;
      if (next >= seconds) { setT(seconds); setPlaying(false); return; }
      setT(next);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps
  // The frame on the authored rate's grid under the slider.
  const frame = Math.min(Math.floor(t * rate.num / rate.den + 1e-9), Math.max(0, Math.round(seconds * rate.num / rate.den) - 1));
  const template = { id: g.templateId, version: g.version, manifest: g.manifest, checksumSha256: g.checksumSha256, previewUrl: null, engine: 'ograf-v1' as const, ograf: m };
  const size = ografResolution(m);
  return (
    <div className="graphic-asset" data-testid="graphic-asset" data-engine="ograf-v1">
      <div className="graphic-stage">
        <OgrafPreview template={template} data={data} schedule={schedule} rate={rate} ms={ografTimestamp(frame, rate)} className="graphic-stage-frame" />
      </div>
      <div className="graphic-controls">
        <button className="btn icon sm" aria-label={playing ? 'Pause' : 'Play'} onClick={() => { if (!playing && t >= seconds) setT(0); setPlaying(!playing); }} data-testid="graphic-play">
          {playing ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <input type="range" min={0} max={seconds} step={rate.den / rate.num} value={t} onChange={(e) => { setPlaying(false); setT(Number(e.target.value)); }} aria-label="Time in the graphic" style={{ flex: 1 }} />
        <span className="mono faint" style={{ fontSize: 12, minWidth: 80, textAlign: 'right' }}>{t.toFixed(2)} / {seconds.toFixed(2)} s</span>
      </div>
      {!g.enabled && <div className="banner warning"><div className="banner-body">This graphic is in the recycle bin: sequences that use it keep rendering it, but it is not offered for new ones.</div></div>}
      <div className="graphic-panels">
        <section>
          <p className="section-label">Fields</p>
          {fields.length === 0 && <p className="muted" style={{ margin: 0 }}>This graphic has no fields.</p>}
          {fields.map((f) => (
            <div className="field" key={f.id}>
              <label htmlFor={`gf-${f.id}`}>{f.title}{f.hidden ? <span className="faint"> · hidden</span> : null}</label>
              {f.kind === 'color' ? <input id={`gf-${f.id}`} type="color" className="input" value={String(data[f.id] ?? f.default)} onChange={(e) => setData({ ...data, [f.id]: e.target.value.toLowerCase() })} style={{ width: 64, padding: 2 }} />
                : f.kind === 'boolean' ? <input id={`gf-${f.id}`} type="checkbox" checked={data[f.id] === true} onChange={(e) => setData({ ...data, [f.id]: e.target.checked })} />
                : f.kind === 'number' ? <input id={`gf-${f.id}`} className="input" type="number" min={f.minimum} max={f.maximum} value={String(data[f.id] ?? '')} onChange={(e) => setData({ ...data, [f.id]: Number(e.target.value) })} />
                : f.kind === 'select' || f.kind === 'image' ? <select id={`gf-${f.id}`} className="select" value={String(data[f.id] ?? '')} onChange={(e) => setData({ ...data, [f.id]: e.target.value })}>{f.options!.map((o) => <option key={String(o)} value={String(o)}>{String(o)}</option>)}</select>
                : f.kind === 'text' ? <input id={`gf-${f.id}`} className="input" maxLength={f.maxLength} value={String(data[f.id] ?? '')} onChange={(e) => setData({ ...data, [f.id]: e.target.value })} data-testid={`graphic-field-${f.id}`} />
                : <span className="faint" style={{ fontSize: 12 }}>Set in PRISM Graphics</span>}
            </div>
          ))}
          <span className="help">Try values here. The defaults that new clips get are part of the graphic: change them in PRISM Graphics and save a new version.</span>
        </section>
        <section>
          <p className="section-label">Graphic</p>
          <dl className="kv" data-testid="graphic-manifest">
            <dt>Name</dt><dd>{m.name}</dd>
            <dt>Description</dt><dd>{m.description || '—'}</dd>
            <dt>Engine</dt><dd>OGraf v1{m.v_editor ? ' · made in PRISM Graphics' : ''}</dd>
            <dt>Frame</dt><dd>{size.width}×{size.height} · {ografAuthoredRate(m) ? `${frameRateLabel(rate)} fps` : 'any rate'}</dd>
            <dt>Steps</dt><dd>{ografStepCount(m) === 0 ? 'A sting: plays in, holds and goes out by itself' : ografStepCount(m)}</dd>
            <dt>Length</dt><dd>{seconds.toFixed(2)} s by default · poster at {(ografPosterMs(m, rate) / 1000).toFixed(2)} s</dd>
            <dt>Package</dt><dd className="mono">{m.id}{m.version ? ` v${m.version}` : ''}</dd>
            <dt>Template</dt><dd className="mono">{g.templateId} v{g.version} · {g.checksumSha256.slice(0, 12)}…</dd>
          </dl>
        </section>
      </div>
    </div>
  );
}

function TemplateAsset({ asset }: { asset: AssetDetail }) {
  const g = asset.graphic!;
  const m = g.manifest;
  const qc = useQueryClient();
  const me = useMe().data;
  const canEdit = Boolean(me?.permissions.includes('asset.edit_metadata')) && !asset.deleted;
  const bundle = useTemplateBundle(g.templateId, g.version);
  // Keyed by the values, so a refetch of the asset does not throw away what is being typed.
  const defaultsKey = JSON.stringify(m.fields.map((f) => [f.id, f.default]));
  const defaults = useMemo(() => Object.fromEntries(m.fields.map((f) => [f.id, f.default])), [defaultsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [data, setData] = useState<Record<string, string>>(defaults);
  useEffect(() => setData(defaults), [defaults]);
  const [t, setT] = useState(m.previewTimeSeconds);
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!playing) return;
    const start = performance.now() - t * 1000;
    let raf = 0;
    const step = () => {
      const next = (performance.now() - start) / 1000;
      if (next >= m.defaultDurationSeconds) { setT(m.defaultDurationSeconds); setPlaying(false); return; }
      setT(next);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = m.fields.some((f) => data[f.id] !== f.default);

  async function saveDefaults() {
    setSaving(true);
    try {
      await api(`/api/v1/assets/${asset.id}/graphic/defaults`, { method: 'PUT', json: { values: data } });
      await qc.invalidateQueries({ queryKey: queryKeys.asset(asset.id) });
      await qc.invalidateQueries({ queryKey: queryKeys.templates() });
      toast('Saved as the defaults for new uses of this graphic');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="graphic-asset" data-testid="graphic-asset">
      <div className="graphic-stage">
        {bundle.data
          ? <GraphicPreview bundle={bundle.data} manifest={m} data={data} t={t} durationSeconds={m.defaultDurationSeconds} className="graphic-stage-frame" />
          : <div className="skeleton" style={{ aspectRatio: `${m.width} / ${m.height}` }} />}
      </div>
      <div className="graphic-controls">
        <button className="btn icon sm" aria-label={playing ? 'Pause' : 'Play'} onClick={() => { if (!playing && t >= m.defaultDurationSeconds) setT(0); setPlaying(!playing); }} data-testid="graphic-play">
          {playing ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <input type="range" min={0} max={m.defaultDurationSeconds} step={0.04} value={t} onChange={(e) => { setPlaying(false); setT(Number(e.target.value)); }} aria-label="Time in the graphic" style={{ flex: 1 }} />
        <span className="mono faint" style={{ fontSize: 12, minWidth: 80, textAlign: 'right' }}>{t.toFixed(2)} / {m.defaultDurationSeconds.toFixed(2)} s</span>
      </div>
      {!g.enabled && <div className="banner warning"><div className="banner-body">This graphic is in the recycle bin: sequences that use it keep rendering it, but it is not offered for new ones.</div></div>}
      <div className="graphic-panels">
        <section>
          <p className="section-label">Fields</p>
          {m.fields.length === 0 && <p className="muted" style={{ margin: 0 }}>This template has no fields.</p>}
          {m.fields.map((f) => (
            <div className="field" key={f.id}>
              <label htmlFor={`gf-${f.id}`}>{f.label}{f.required ? ' *' : ''}</label>
              {f.type === 'color'
                ? <input id={`gf-${f.id}`} type="color" className="input" value={data[f.id] ?? f.default} onChange={(e) => setData({ ...data, [f.id]: e.target.value })} style={{ width: 64, padding: 2 }} />
                : <input id={`gf-${f.id}`} className="input" value={data[f.id] ?? ''} maxLength={f.maxLength} onChange={(e) => setData({ ...data, [f.id]: e.target.value })} data-testid={`graphic-field-${f.id}`} />}
            </div>
          ))}
          {canEdit && m.fields.length > 0 && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn sm primary" disabled={!changed || saving} onClick={() => void saveDefaults()} data-testid="graphic-save-defaults"><Save size={13} /> Save as defaults</button>
              <button className="btn sm ghost" disabled={!changed} onClick={() => setData(defaults)}>Reset</button>
            </div>
          )}
          <span className="help">Defaults fill the fields when the graphic is added to a sequence. They do not change sequences that already use it.</span>
        </section>
        <section>
          <p className="section-label">Template</p>
          <dl className="kv" data-testid="graphic-manifest">
            <dt>Name</dt><dd>{m.name}</dd>
            <dt>Description</dt><dd>{m.description || '—'}</dd>
            <dt>Kind</dt><dd>{KIND_LABEL[m.kind]}</dd>
            <dt>Frame</dt><dd>{m.width}×{m.height} · safe area {m.safeArea.width}×{m.safeArea.height} at {m.safeArea.x},{m.safeArea.y}</dd>
            <dt>Length</dt><dd>{m.defaultDurationSeconds} s · in {m.animation.inSeconds} s, out {m.animation.outSeconds} s · poster at {m.previewTimeSeconds} s</dd>
            <dt>Fonts</dt><dd>{m.fonts.length ? m.fonts.map((f) => `${f.family} (${f.license})`).join(', ') : 'None bundled'}</dd>
            <dt>Package</dt><dd className="mono">{g.packageId ?? '—'}{g.packageVersion != null ? ` v${g.packageVersion}` : ''}</dd>
            <dt>Template</dt><dd className="mono">{g.templateId} v{g.version} · {g.checksumSha256.slice(0, 12)}…</dd>
          </dl>
        </section>
      </div>
    </div>
  );
}
